/**
 * English and Arabic resources (spec §2).
 *
 * Both locales are first-class from the first milestone. Arabic is not a
 * translation layer added later — it is a supported interface direction, which
 * is why `dir` lives here beside the strings.
 *
 * Rules that matter clinically:
 *   - Clinical text is stored AS ENTERED and never machine-translated.
 *   - Dates and numbers are formatted per locale, but the stored instant is
 *     always the same UTC value.
 *   - The dental chart's orientation is NOT part of the direction switch:
 *     the patient's right stays on the viewer's left in both languages.
 */
import { MODULE_DICTIONARIES, type ModuleDict } from './modules.js';

export type Locale = 'en' | 'ar';
export type Direction = 'ltr' | 'rtl';

export const LOCALES: Locale[] = ['en', 'ar'];

export const DIRECTION: Record<Locale, Direction> = { en: 'ltr', ar: 'rtl' };

export const LOCALE_LABEL: Record<Locale, string> = { en: 'English', ar: 'العربية' };

const en = {
  app: { name: 'OxyFlows', tagline: 'Dental clinic management' },
  /* The brand's own lines (Brand Guidelines 2026). */
  brand: {
    tagline: 'Medical intelligence in flow',
    motto: ['People.', 'Technology.', 'Better care.'],
    overview:
      'OxyFlows is a smart dental clinic management platform designed to connect people, patients and processes within one seamless system.',
    poweredBy: 'Powered by',
  },
  nav: {
    workspace: 'Workspace',
    dashboard: 'Dashboard',
    patients: 'Patients',
    appointments: 'Appointments',
    finance: 'Finance',
    dentists: 'Dentists',
    practitioners: 'Practitioners',
    laboratory: 'Laboratory',
    inventory: 'Inventory',
    clinic: 'Clinic',
    reports: 'Reports',
    sync: 'Offline activity',
  },
  auth: {
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signOut: 'Sign out',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'you@clinic.example',
    invalid: 'Email or password is incorrect.',
    chooseClinic: 'Choose a clinic',
    chooseClinicHint: 'Your account has access to more than one clinic.',
    demoTitle: 'Demo accounts',
    demoHint: 'Local development only — every account uses the password below.',
    activateTitle: 'Activate your account',
    activateHint: 'Use your clinic’s activation code and choose a password. We then email you a six-digit code to confirm the address.',
    code: 'Activation code',
    fullName: 'Your name',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    passwordsDiffer: 'The two passwords do not match.',
    passwordRule: 'At least 10 characters. Length is what matters.',
    activate: 'Activate',
    haveCode: 'Have an activation code?',
    backToSignIn: 'Back to sign in',
    currentPassword: 'Current password',
    changePassword: 'Change password',
    passwordChanged: 'Password changed. Your other sessions were signed out.',
    registerTitle: 'Create your account',
    registerHint: 'A clinic invited you by email. Use that address and choose a password; we then email you a six-digit code to confirm it.',
    createAccount: 'Create account',
    invitedLink: 'Invited by a clinic? Create your account',
    ownerLink: 'Clinic owner? Activate with your clinic code',
    policyLink: 'Data & privacy policy',
    codeTitle: 'Check your email',
    codeHint: 'We sent a six-digit code to {to}. It works for 10 minutes.',
    codeLabel: 'Code',
    verify: 'Verify',
    trustDevice: 'Trust this device for 30 days',
    trustHint: 'You will not be asked for a code on this browser for 30 days. Leave it unticked on a shared computer.',
    resend: 'Send a new code',
    resendIn: 'Send a new code in {s} s',
    codeSent: 'A new code is on its way.',
    devCode: 'Development build: the code is {code}',
    forgotLink: 'Forgot your password?',
    forgotTitle: 'Reset your password',
    forgotHint: 'Enter your email address. If it has an account, we will send a code to reset the password.',
    sendCode: 'Send the code',
    resetTitle: 'Choose a new password',
    resetHint: 'Enter the code we sent to {to}, then a new password.',
    resetButton: 'Save the new password',
    acceptPolicy: 'On behalf of the clinic, I accept the data policy, including that its records are stored on servers in the European Union.',
    blockedTitle: 'This clinic is closed for now',
    blocked: {
      expired: 'The clinic\'s subscription has ended.',
      paused: 'Access is paused until the subscription is settled.',
      suspended: 'The clinic has been suspended by OxyFlows.',
      closed: 'The clinic has been closed.',
    },
    blockedOwnerHint: 'Nothing has been lost. Contact OxyFlows to renew, and everything is where you left it.',
    blockedUserHint: 'Nothing has been lost. Ask the clinic owner — access returns when the subscription is settled.',
    until: 'Until',
    expiresSoon: 'The subscription ends on {date} ({days} days). Contact OxyFlows to renew before then.',
    expiresSoonUser: 'The subscription ends on {date} ({days} days).',
  },
  clinic: { switch: 'Switch clinic', current: 'Current clinic', plan: 'Plan' },
  dashboard: {
    title: 'Dashboard',
    greeting: 'Good day, {name}',
    welcomeBack: 'Welcome back',
    todayIntro: "Here's what's happening in your clinic today.",
    uniquePatients: 'Patients',
    uniquePatientsHint: 'Unique patient records you can access',
    appointmentsToday: 'Appointments today',
    upcoming: 'Upcoming',
    upcomingHint: 'Scheduled after now',
    unbilledWork: 'Unbilled work',
    unbilledWorkHint: 'Completed procedures not yet on an invoice',
    alerts: 'Active medical alerts',
    todaySchedule: "Today's schedule",
    noAppointments: 'No appointments scheduled for today.',
    recentActivity: 'Recent activity',
    noActivity: 'Nothing recorded yet.',
    attention: 'Needs attention',
    nothingNeedsAttention: 'Nothing needs you right now.',
    assignedOnly: 'You see patients assigned to you.',
  },
  patients: {
    archived: 'Archived',
    title: 'Patients',
    search: 'Search by name, number or phone',
    add: 'Add patient',
    none: 'No patients match that search.',
    noneYet: 'No patients yet. Add the first one.',
    number: 'Number',
    name: 'Name',
    born: 'Date of birth',
    phone: 'Phone',
    actions: 'Actions',
    view: 'Open',
    count: '{count} patients',
    newTitle: 'New patient',
    givenName: 'Given name',
    familyName: 'Family name',
    givenNameAlt: 'Given name (Arabic)',
    familyNameAlt: 'Family name (Arabic)',
    birthDate: 'Date of birth',
    sex: 'Sex',
    whatsapp: 'WhatsApp number',
    emailAddress: 'Email',
    guardian: 'Guardian',
    guardianName: 'Guardian name',
    guardianRelation: 'Relationship',
    guardianPhone: 'Guardian phone',
    notes: 'Notes',
    save: 'Save patient',
    saving: 'Saving…',
    cancel: 'Cancel',
    optional: 'optional',
    sharedContactHint: 'Phone and email are not unique — families often share them.',
    medicalHistory: 'Medical history',
    allergies: 'Allergies',
    alerts: 'Medical alerts',
    recentAppointments: 'Recent appointments',
    noAllergies: 'No allergies recorded.',
    noAlerts: 'No medical alerts.',
    allergyStatus: {
      not_recorded: 'Allergy status not recorded',
      none_known: 'No known allergies',
      positive: 'Known allergies',
    },
    overview: 'Overview',
    account: 'Account',
    timeline: 'History',
    noHistory: 'Nothing recorded yet.',
    timelineFiltered:
      'Some entries are not shown because your role does not include them. This is a filtered view, not the whole history.',
    allergyStatusHint: '“Not recorded” is not the same as “no known allergies”.',
    editMedicalHistory: 'Record medical history',
    addAllergy: 'Add an allergy',
    substance: 'Substance',
    reaction: 'Reaction',
    medications: 'Current medicines',
    addMedication: 'Add a medicine',
    medicalNotes: 'Notes',
    reviewedAt: 'Reviewed',
    noMedications: 'No medicines recorded.',
    severity: 'Severity',
    medicalHistoryHint:
      'Recording “no known allergies” is a clinical statement made on purpose, and it is audited under your name. The safety check refuses to prescribe while the status is “not recorded”.',
  },
  appointments: {
    title: 'Appointments',
    today: 'Today',
    add: 'Book appointment',
    none: 'Nothing booked for this day.',
    time: 'Time',
    patient: 'Patient',
    dentist: 'Dentist',
    chair: 'Chair',
    reason: 'Reason',
    status: 'Status',
    newTitle: 'Book an appointment',
    date: 'Date',
    startTime: 'Start time',
    duration: 'Duration',
    minutes: '{n} minutes',
    book: 'Book',
    booking: 'Booking…',
    provisional: 'Provisional',
    overlap: 'Overlaps another booking — made offline; move it',
    slotTaken: 'That time overlaps an existing appointment. Pick another slot.',
    previous: 'Previous day',
    next: 'Next day',
    views: { day: 'Day', week: 'Week', month: 'Month' },
    splitBy: 'Split by',
    byDentist: 'By dentist',
    byChair: 'By chair',
    allChairs: 'All chairs',
    noChair: 'No chair',
    filterPatient: 'Find a patient',
    filtered: 'Showing a filtered view. Some appointments are hidden.',
    clearFilters: 'Clear filters',
    more: 'more',
    cancel: 'Cancel appointment',
    cancelReason: 'Why is it being cancelled?',
    cancelNote:
      'The appointment stays in the record with its reason, and the slot becomes free again. Nothing is deleted.',
    reschedule: 'Reschedule',
    whatsapp: 'WhatsApp reminder',
    whatsappHint: 'Opens a ready-to-send message for the patient, in their language.',
    rescheduleNote:
      'The same appointment at a new time — not a cancellation and a rebooking. Overlaps are still refused.',
    moveTo: 'Move to',
    patientHint: 'Leave empty for a walk-in or a call you have not matched to a record yet.',
    durationHint: 'The slot this takes on the calendar.',
    states: {
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      checked_in: 'Checked in',
      in_treatment: 'In treatment',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No show',
    },
  },
  sex: { female: 'Female', male: 'Male', other: 'Other', unspecified: 'Not specified' },
  severity: { mild: 'Mild', moderate: 'Moderate', severe: 'Severe', unknown: 'Unknown', low: 'Low', high: 'High' },
  common: {
    loading: 'Loading…',
    retry: 'Try again',
    error: 'Something went wrong.',
    offline: 'You appear to be offline.',
    required: 'Required',
    close: 'Close',
    language: 'Language',
    notRecorded: 'Not recorded',
    status: 'Status',
    years: '{n} yrs',
  },
  desktop: {
    online: 'Connected',
    offline: 'Working offline',
    offlineNote: 'Changes are kept on this computer and sent when the connection returns.',
    pending: '{n} change(s) waiting to be sent',
    allSent: 'Everything is sent',
    conflicts: '{n} need(s) a look',
    lastSync: 'Last sync {at}',
    neverSynced: 'Not synced yet',
    leaseDays: 'Offline licence: {n} day(s) left',
    leaseMissing: 'No offline licence yet',
    leaseExpired: 'Offline licence ended — connect to renew',
    leaseInvalid: 'Offline licence not valid on this computer',
    syncNow: 'Sync now',
    syncing: 'Syncing…',
    syncProblem: 'Last sync had a problem',
    toLookAt: '{n} to look at',
    offlineCached: 'Shown as last seen online',
    clockOffset: 'This computer\'s clock is {n} min off the server; offline work is timed on the server\'s clock when sent.',
  },
  errors: {
    network: 'Cannot reach the server. Is the API running?',
    permission: 'Your role in this clinic does not allow that.',
    entitlement: 'The clinic plan does not include this feature.',
    notFound: 'Not found.',
    validation: 'Some details need correcting.',
  },
};

/**
 * The shape every locale must satisfy. Note there is no `as const` above: the
 * values are plain strings, so what Arabic has to match is the set of KEYS, not
 * the English wording. A missing key is a compile error rather than an English
 * sentence appearing mid-sentence on an Arabic screen.
 */
type Dict = typeof en;

/**
 * Arabic. Written as a peer of English rather than a partial override, so a
 * missing key is a type error instead of a silent fallback to English in the
 * middle of an Arabic screen.
 */
const ar: Dict = {
  app: { name: 'أُكسي فلوز', tagline: 'إدارة عيادات الأسنان' },
  brand: {
    tagline: 'ذكاء طبي بانسيابية',
    motto: ['الإنسان.', 'التقنية.', 'رعاية أفضل.'],
    overview: 'أُكسي فلوز منصة ذكية لإدارة عيادات الأسنان، صُمّمت لتربط الأشخاص والمرضى والإجراءات في نظام واحد متكامل.',
    poweredBy: 'مدعوم من',
  },
  nav: {
    workspace: 'مساحة العمل',
    dashboard: 'لوحة المعلومات',
    patients: 'المرضى',
    appointments: 'المواعيد',
    finance: 'المالية',
    dentists: 'الأطباء',
    practitioners: 'الممارسون',
    laboratory: 'المخبر',
    inventory: 'المستودع',
    clinic: 'العيادة',
    reports: 'التقارير',
    sync: 'ما جرى دون اتصال',
  },
  auth: {
    signIn: 'تسجيل الدخول',
    signingIn: 'جارٍ تسجيل الدخول…',
    signOut: 'تسجيل الخروج',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    emailPlaceholder: 'you@clinic.example',
    invalid: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
    chooseClinic: 'اختر العيادة',
    chooseClinicHint: 'لحسابك صلاحية الوصول إلى أكثر من عيادة.',
    demoTitle: 'حسابات تجريبية',
    demoHint: 'بيئة تطوير محلية فقط — جميع الحسابات تستخدم كلمة المرور أدناه.',
    activateTitle: 'تفعيل حسابك',
    activateHint: 'استخدم رمز تفعيل عيادتك واختر كلمة مرور. سنرسل بعدها رمزًا من ستة أرقام إلى بريدك لتأكيد العنوان.',
    code: 'رمز التفعيل',
    fullName: 'اسمك',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور',
    passwordsDiffer: 'كلمتا المرور غير متطابقتين.',
    passwordRule: '10 أحرف على الأقل. الطول هو ما يهم.',
    activate: 'تفعيل',
    haveCode: 'لديك رمز تفعيل؟',
    backToSignIn: 'العودة إلى تسجيل الدخول',
    currentPassword: 'كلمة المرور الحالية',
    changePassword: 'تغيير كلمة المرور',
    passwordChanged: 'تم تغيير كلمة المرور، وتم تسجيل الخروج من جلساتك الأخرى.',
    registerTitle: 'إنشاء حسابك',
    registerHint: 'دعتك عيادة عبر البريد الإلكتروني. استخدم العنوان نفسه واختر كلمة مرور؛ سنرسل بعدها رمزًا من ستة أرقام إلى بريدك لتأكيده.',
    createAccount: 'إنشاء الحساب',
    invitedLink: 'دعتك عيادة؟ أنشئ حسابك',
    ownerLink: 'مالك عيادة؟ فعّل برمز عيادتك',
    policyLink: 'سياسة البيانات والخصوصية',
    codeTitle: 'تحقّق من بريدك',
    codeHint: 'أرسلنا رمزًا من ستة أرقام إلى {to}. الرمز صالح لمدة 10 دقائق.',
    codeLabel: 'الرمز',
    verify: 'تحقّق',
    trustDevice: 'الوثوق بهذا الجهاز لمدة 30 يومًا',
    trustHint: 'لن يُطلب منك رمز على هذا المتصفح لمدة 30 يومًا. لا تحدد هذا الخيار على جهاز مشترك.',
    resend: 'أرسل رمزًا جديدًا',
    resendIn: 'أرسل رمزًا جديدًا بعد {s} ث',
    codeSent: 'الرمز الجديد في طريقه إليك.',
    devCode: 'نسخة التطوير: الرمز هو {code}',
    forgotLink: 'نسيت كلمة المرور؟',
    forgotTitle: 'إعادة تعيين كلمة المرور',
    forgotHint: 'أدخل بريدك الإلكتروني. إن كان له حساب، سنرسل رمزًا لإعادة تعيين كلمة المرور.',
    sendCode: 'أرسل الرمز',
    resetTitle: 'اختر كلمة مرور جديدة',
    resetHint: 'أدخل الرمز الذي أرسلناه إلى {to}، ثم كلمة مرور جديدة.',
    resetButton: 'احفظ كلمة المرور الجديدة',
    acceptPolicy: 'بالنيابة عن العيادة، أوافق على سياسة البيانات، بما في ذلك تخزين سجلاتها على خوادم في الاتحاد الأوروبي.',
    blockedTitle: 'هذه العيادة مغلقة حالياً',
    blocked: {
      expired: 'انتهى اشتراك العيادة.',
      paused: 'الوصول موقوف مؤقتاً حتى تسوية الاشتراك.',
      suspended: 'علّقت أُكسي فلوز هذه العيادة.',
      closed: 'أُغلقت هذه العيادة.',
    },
    blockedOwnerHint: 'لم يُفقد شيء. تواصل مع أُكسي فلوز للتجديد وستجد كل شيء كما تركته.',
    blockedUserHint: 'لم يُفقد شيء. اسأل مالك العيادة — يعود الوصول عند تسوية الاشتراك.',
    until: 'حتى',
    expiresSoon: 'ينتهي الاشتراك في {date} (بعد {days} يوماً). تواصل مع أُكسي فلوز للتجديد قبل ذلك.',
    expiresSoonUser: 'ينتهي الاشتراك في {date} (بعد {days} يوماً).',
  },
  clinic: { switch: 'تبديل العيادة', current: 'العيادة الحالية', plan: 'الباقة' },
  dashboard: {
    title: 'لوحة المعلومات',
    greeting: 'أهلاً، {name}',
    welcomeBack: 'مرحبًا بعودتك',
    todayIntro: 'إليك ما يجري في عيادتك اليوم.',
    uniquePatients: 'المرضى',
    uniquePatientsHint: 'عدد سجلات المرضى التي يمكنك الاطلاع عليها',
    appointmentsToday: 'مواعيد اليوم',
    upcoming: 'القادمة',
    upcomingHint: 'المجدولة بعد الآن',
    unbilledWork: 'أعمال غير مفوترة',
    unbilledWorkHint: 'إجراءات مكتملة لم توضع على فاتورة بعد',
    alerts: 'تنبيهات طبية فعّالة',
    todaySchedule: 'جدول اليوم',
    noAppointments: 'لا توجد مواعيد مجدولة اليوم.',
    recentActivity: 'النشاط الأخير',
    noActivity: 'لا يوجد نشاط مسجّل بعد.',
    attention: 'يحتاج انتباهاً',
    nothingNeedsAttention: 'لا شيء يحتاجك الآن.',
    assignedOnly: 'تظهر لك المرضى المُسندون إليك فقط.',
  },
  patients: {
    archived: 'مؤرشف',
    title: 'المرضى',
    search: 'ابحث بالاسم أو الرقم أو الهاتف',
    add: 'إضافة مريض',
    none: 'لا يوجد مرضى مطابقون للبحث.',
    noneYet: 'لا يوجد مرضى بعد. أضف أول مريض.',
    number: 'الرقم',
    name: 'الاسم',
    born: 'تاريخ الميلاد',
    phone: 'الهاتف',
    actions: 'إجراءات',
    view: 'فتح',
    count: '{count} مريض',
    newTitle: 'مريض جديد',
    givenName: 'الاسم الأول',
    familyName: 'اسم العائلة',
    givenNameAlt: 'الاسم الأول (بالعربية)',
    familyNameAlt: 'اسم العائلة (بالعربية)',
    birthDate: 'تاريخ الميلاد',
    sex: 'الجنس',
    whatsapp: 'رقم واتساب',
    emailAddress: 'البريد الإلكتروني',
    guardian: 'ولي الأمر',
    guardianName: 'اسم ولي الأمر',
    guardianRelation: 'صلة القرابة',
    guardianPhone: 'هاتف ولي الأمر',
    notes: 'ملاحظات',
    save: 'حفظ المريض',
    saving: 'جارٍ الحفظ…',
    cancel: 'إلغاء',
    optional: 'اختياري',
    sharedContactHint: 'الهاتف والبريد غير فريدين — غالباً ما تتشاركهما العائلة.',
    medicalHistory: 'التاريخ الطبي',
    allergies: 'الحساسية',
    alerts: 'تنبيهات طبية',
    recentAppointments: 'المواعيد الأخيرة',
    noAllergies: 'لا توجد حساسية مسجّلة.',
    noAlerts: 'لا توجد تنبيهات طبية.',
    allergyStatus: {
      not_recorded: 'حالة الحساسية غير مسجّلة',
      none_known: 'لا توجد حساسية معروفة',
      positive: 'توجد حساسية معروفة',
    },
    overview: 'نظرة عامة',
    account: 'الحساب',
    timeline: 'السجل',
    noHistory: 'لا يوجد شيء مسجَّل بعد.',
    timelineFiltered: 'بعض القيود غير معروضة لأن دورك لا يشملها. هذا عرض مُرشَّح وليس السجل كاملاً.',
    allergyStatusHint: '«غير مسجّلة» لا تعني «لا توجد حساسية معروفة».',
    editMedicalHistory: 'تسجيل التاريخ الطبي',
    addAllergy: 'إضافة حساسية',
    substance: 'المادة',
    reaction: 'التفاعل',
    medications: 'الأدوية الحالية',
    addMedication: 'إضافة دواء',
    medicalNotes: 'ملاحظات',
    reviewedAt: 'رُوجع',
    noMedications: 'لا أدوية مسجّلة.',
    severity: 'الشدة',
    medicalHistoryHint:
      'تسجيل «لا توجد حساسية معروفة» قرار سريري متعمّد ويُسجَّل باسمك. يرفض فحص السلامة الوصف ما دامت الحالة «غير مسجّلة».',
  },
  appointments: {
    title: 'المواعيد',
    today: 'اليوم',
    add: 'حجز موعد',
    none: 'لا توجد مواعيد في هذا اليوم.',
    time: 'الوقت',
    patient: 'المريض',
    dentist: 'الطبيب',
    chair: 'الكرسي',
    reason: 'السبب',
    status: 'الحالة',
    newTitle: 'حجز موعد',
    date: 'التاريخ',
    startTime: 'وقت البدء',
    duration: 'المدة',
    minutes: '{n} دقيقة',
    book: 'حجز',
    booking: 'جارٍ الحجز…',
    provisional: 'مبدئي',
    overlap: 'يتداخل مع موعد آخر — حُجز دون اتصال؛ انقله',
    slotTaken: 'هذا الوقت يتداخل مع موعد قائم. اختر وقتاً آخر.',
    previous: 'اليوم السابق',
    next: 'اليوم التالي',
    views: { day: 'يوم', week: 'أسبوع', month: 'شهر' },
    splitBy: 'التقسيم حسب',
    byDentist: 'حسب الطبيب',
    byChair: 'حسب الكرسي',
    allChairs: 'كل الكراسي',
    noChair: 'بدون كرسي',
    filterPatient: 'ابحث عن مريض',
    filtered: 'العرض مُرشَّح. بعض المواعيد مخفية.',
    clearFilters: 'إزالة الترشيح',
    more: 'أخرى',
    cancel: 'إلغاء الموعد',
    cancelReason: 'ما سبب الإلغاء؟',
    cancelNote: 'يبقى الموعد في السجل مع سببه، ويعود الوقت متاحاً. لا يُحذف شيء.',
    reschedule: 'إعادة جدولة',
    whatsapp: 'تذكير واتساب',
    whatsappHint: 'يفتح رسالة جاهزة للإرسال للمريض بلغته.',
    rescheduleNote: 'الموعد نفسه في وقت جديد — وليس إلغاءً وحجزاً جديداً. التعارض مرفوض كما هو.',
    moveTo: 'الانتقال إلى',
    patientHint: 'اتركه فارغاً لمريض بلا موعد أو لمكالمة لم تُربط بسجل بعد.',
    durationHint: 'المدة التي يشغلها على التقويم.',
    states: {
      scheduled: 'مجدول',
      confirmed: 'مؤكد',
      checked_in: 'تم الحضور',
      in_treatment: 'قيد المعالجة',
      completed: 'مكتمل',
      cancelled: 'ملغى',
      no_show: 'لم يحضر',
    },
  },
  sex: { female: 'أنثى', male: 'ذكر', other: 'آخر', unspecified: 'غير محدد' },
  severity: { mild: 'خفيفة', moderate: 'متوسطة', severe: 'شديدة', unknown: 'غير معروفة', low: 'منخفضة', high: 'مرتفعة' },
  common: {
    loading: 'جارٍ التحميل…',
    retry: 'إعادة المحاولة',
    error: 'حدث خطأ ما.',
    offline: 'يبدو أنك غير متصل.',
    required: 'مطلوب',
    close: 'إغلاق',
    language: 'اللغة',
    notRecorded: 'غير مسجّل',
    status: 'الحالة',
    years: '{n} سنة',
  },
  desktop: {
    online: 'متصل',
    offline: 'العمل دون اتصال',
    offlineNote: 'تُحفظ التغييرات على هذا الحاسوب وتُرسل عند عودة الاتصال.',
    pending: '{n} تغيير بانتظار الإرسال',
    allSent: 'أُرسل كل شيء',
    conflicts: '{n} يحتاج مراجعة',
    lastSync: 'آخر مزامنة {at}',
    neverSynced: 'لم تتم المزامنة بعد',
    leaseDays: 'رخصة العمل دون اتصال: {n} يوماً متبقية',
    leaseMissing: 'لا رخصة عمل دون اتصال بعد',
    leaseExpired: 'انتهت رخصة العمل دون اتصال — اتصل لتجديدها',
    leaseInvalid: 'رخصة العمل دون اتصال غير صالحة على هذا الحاسوب',
    syncNow: 'زامن الآن',
    syncing: 'جارٍ المزامنة…',
    syncProblem: 'واجهت آخر مزامنة مشكلة',
    toLookAt: '{n} تحتاج نظرة',
    offlineCached: 'يُعرض كما شوهد آخر مرة عند الاتصال',
    clockOffset: 'ساعة هذا الحاسوب تختلف عن الخادم بـ {n} دقيقة؛ يُؤرَّخ العمل دون اتصال بساعة الخادم عند إرساله.',
  },
  errors: {
    network: 'تعذّر الوصول إلى الخادم. هل واجهة البرمجة تعمل؟',
    permission: 'دورك في هذه العيادة لا يسمح بذلك.',
    entitlement: 'باقة العيادة لا تتضمن هذه الميزة.',
    notFound: 'غير موجود.',
    validation: 'بعض البيانات تحتاج إلى تصحيح.',
  },
};

/**
 * The core dictionary merged with the module dictionaries. Split across two
 * files purely for readability — callers see one object.
 */
export const DICTIONARIES = {
  en: { ...en, ...MODULE_DICTIONARIES.en },
  ar: { ...ar, ...MODULE_DICTIONARIES.ar },
} satisfies Record<Locale, Dict & ModuleDict>;

/** Interpolates {name}-style placeholders. */
export function interpolate(template: string, values: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

/**
 * Locale-aware formatting. The stored value never changes — only its
 * presentation. Arabic uses Latin digits here deliberately: clinics in the
 * region routinely mix scripts, and phone numbers and record identifiers stay
 * readable in either direction (spec §2).
 */
/**
 * A bare `YYYY-MM-DD` is a calendar date, not an instant. `new Date()` reads
 * it as UTC midnight, which a viewer west of UTC would see as the previous
 * evening — a birthday a day early. Such strings are built as LOCAL dates so
 * the day shown is the day written, in every timezone.
 */
function parseDateValue(value: string | Date): Date {
  if (value instanceof Date) return value;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(value);
}

export function formatDate(value: string | Date | null | undefined, locale: Locale): string {
  if (!value) return '';
  const d = parseDateValue(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SY-u-nu-latn' : 'en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

export function formatTime(value: string | Date, locale: Locale, timeZone?: string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SY-u-nu-latn' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SY-u-nu-latn' : 'en-GB').format(value);
}

/** Whole years, from a yyyy-mm-dd date of birth. */
export function ageFrom(birthDate: string | null | undefined): number | null {
  if (!birthDate) return null;
  const dob = new Date(birthDate);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const m = now.getUTCMonth() - dob.getUTCMonth();
  if (m < 0 || (m === 0 && now.getUTCDate() < dob.getUTCDate())) age--;
  return age >= 0 ? age : null;
}
